import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, MessageSquare, Phone, Activity, Lock, Radio } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";

const ICONS = { email: Mail, sms: MessageSquare, voice: Phone };
const COLORS = { email: "#2A4B46", sms: "#3b6ea5", voice: "#D36B46" };
const CHANNELS = [
  { v: "", l: "All" },
  { v: "email", l: "Email" },
  { v: "sms", l: "SMS" },
  { v: "voice", l: "Voice" },
];

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function DispatchLog({ open, onClose, onCountChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const access = user?.role === "admin" || user?.business_active;
  const [items, setItems] = useState([]);
  const [channel, setChannel] = useState("");
  const [onDate, setOnDate] = useState("");

  const load = useCallback(async () => {
    if (!access) return;
    const params = {};
    if (channel) params.channel = channel;
    if (onDate) params.on_date = onDate;
    try {
      const { data } = await api.get("/dispatches", { params });
      setItems(data);
      if (onCountChange) onCountChange();
    } catch (e) {}
  }, [channel, onDate, onCountChange, access]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const test = async (ch) => {
    try {
      await api.post("/dispatches/test", { channel: ch });
      if (channel && channel !== ch) setChannel("");
      else await load();
    } catch (e) {}
  };

  return (
    <div className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`}>
      <div className={`absolute inset-0 bg-[#1A1A18]/50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <aside
        data-testid="dispatch-panel"
        className={`absolute right-0 top-0 h-full w-full sm:w-[440px] bg-[#F9F9F7] border-l border-[var(--line)] flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
          <div>
            <p className="label-mono text-[var(--text-2)]">Omni-channel</p>
            <h3 className="font-serif text-2xl tracking-tight mt-1 flex items-center gap-2">
              <Activity className="w-5 h-5 text-[var(--accent)]" /> Dispatch Log
            </h3>
          </div>
          <button data-testid="dispatch-close" onClick={onClose} className="p-1 hover:text-[var(--accent)] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!access ? (
          <div data-testid="dispatch-locked" className="flex-1 grid place-items-center p-8 text-center">
            <div className="max-w-xs">
              <div className="w-14 h-14 rounded-2xl bg-[var(--accent)]/10 grid place-items-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-[var(--accent)]" />
              </div>
              <h4 className="font-serif text-2xl">Dispatch is a Business Platinum feature</h4>
              <p className="text-sm text-[var(--text-2)] mt-2">Unlock the omni-channel Dispatch Log, automated customer check-ins and outbound SMS · email · voice.</p>
              <p className="font-serif text-3xl mt-4">$69.99<span className="text-sm text-[var(--text-2)]"> / mo</span></p>
              <button
                data-testid="dispatch-upgrade"
                onClick={() => startCheckout("platinum_monthly").catch(() => navigate("/pricing"))}
                className="mt-5 w-full h-11 flex items-center justify-center gap-2 bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <Radio className="w-4 h-4" /> Upgrade to Business Platinum
              </button>
              <button data-testid="dispatch-see-plans" onClick={() => { onClose(); navigate("/pricing"); }} className="mt-3 text-sm text-[var(--accent)] hover:underline">See all plans</button>
            </div>
          </div>
        ) : (
        <>
        {/* Filters */}
        <div className="p-4 border-b border-[var(--line)] space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map((c) => (
              <button
                key={c.v || "all"}
                data-testid={`filter-channel-${c.v || "all"}`}
                onClick={() => setChannel(c.v)}
                className={`h-8 px-3 text-xs border transition-colors ${channel === c.v ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}
              >
                {c.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              data-testid="filter-date"
              value={onDate}
              onChange={(e) => setOnDate(e.target.value)}
              className="h-8 px-2 text-xs border border-[var(--line)] bg-white"
            />
            {onDate && (
              <button data-testid="filter-date-clear" onClick={() => setOnDate("")} className="text-xs text-[var(--accent)] hover:underline">
                Clear date
              </button>
            )}
            <span className="ml-auto label-mono text-[var(--text-2)]">{items.length} shown</span>
          </div>
        </div>

        <div className="p-4 border-b border-[var(--line)] bg-[#fff2ec]">
          <p className="text-xs text-[var(--text-2)] leading-relaxed">
            <strong className="text-[var(--text)]">Twilio SMS & Voice are LIVE.</strong> Email is simulated until a verified SendGrid sender is added. Send a test:
          </p>
          <div className="flex gap-2 mt-3">
            {["email", "sms", "voice"].map((ch) => (
              <button
                key={ch}
                data-testid={`test-${ch}`}
                onClick={() => test(ch)}
                className="flex-1 h-9 text-xs border border-[var(--line)] bg-white hover:border-[var(--accent)] transition-colors capitalize"
              >
                Test {ch}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin p-4 space-y-3">
          {items.length === 0 && <p className="text-sm text-[var(--text-2)] text-center py-12">No dispatches match these filters.</p>}
          {items.map((d) => {
            const Icon = ICONS[d.channel] || Mail;
            const statusColor = d.status === "sent" ? "#2A4B46" : d.status === "failed" ? "#DC2626" : d.status === "requires_premium" ? "#D36B46" : "#5c5c59";
            return (
              <div key={d.id} data-testid="dispatch-item" className="border border-[var(--line)] bg-white p-3 flex gap-3">
                <div className="w-8 h-8 flex items-center justify-center shrink-0" style={{ backgroundColor: (COLORS[d.channel] || "#333") + "1a" }}>
                  <Icon className="w-4 h-4" style={{ color: COLORS[d.channel] || "#333" }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{d.title || "Reminder"}</span>
                    <span className="text-[10px] font-mono text-[var(--text-2)] shrink-0">{timeAgo(d.created_at)}</span>
                  </div>
                  <p className="text-xs text-[var(--text-2)] mt-0.5 line-clamp-2">{d.message}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="label-mono text-[9px] text-[var(--text-2)]">{d.channel}</span>
                    <span className="text-[10px] font-mono text-[var(--text-2)] truncate">→ {d.to}</span>
                    <span className="ml-auto text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5" style={{ color: statusColor, background: statusColor + "1a" }}>
                      {d.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
        )}
      </aside>
    </div>
  );
}
